// Filename: src/pages/ace-analyzers/AceAnalyzerPage.tsx
import React, { useState, useEffect, ChangeEvent, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Pagination, Modal, Tooltip, Menu, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DownloadModal from 'components/Monitor/Modals/DownloadModal';
import IosShareIcon from '@mui/icons-material/IosShare';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import RunAnalysisModal from 'components/Analyzers/RunAnalysisModal';
import { AnalyzersAPI } from 'api/AnalyzersAPI';
import CreditsAPI from 'api/CreditsAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import { useContext } from 'react';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import DeleteConfirmationModal from 'components/Analyzers/DeleteConfirmationModal';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
// NEW: Import the IEEPA info modal component
import IeepaInfoModal from 'components/Analyzers/IeepaInfoModal';
// NEW: Import the AttachMoneyIcon
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
// NEW: BITE Findings Report (PDF) download icon for the Actions column.
// The PDF itself is generated by the processor at finalize time; this icon
// just presigns the existing object via AnalyzersAPI.getBiteReportDownloadUrl.
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

const AceAnalyzerPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('All Runs');
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [newAnalysisModal, setNewAnalysisModal] = useState(false);
  // NEW: State for the new IEEPA info modal
  const [ieepaInfoModalOpen, setIeepaInfoModalOpen] = useState(false);
  const [runs, setRuns] = useState<any[]>([]);
  const [, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showAceGuide, setShowAceGuide] = useState(false);
  const [runToDelete, setRunToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({
    key: 'created_at',
    direction: 'desc',
  });

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key: key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const processedRuns = useMemo(() => {
    let result = [...runs];

    // 1. Tab Filter
    if (activeTab === 'Completed') {
      result = result.filter(
        (r) => r.status === 'Complete' || r.status === 'Completed',
      );
    } else if (activeTab === 'Running') {
      result = result.filter(
        (r) => r.status === 'Running' || r.status === 'Pending',
      );
    } else if (activeTab === 'Needs Review') {
      result = result.filter((r) => r.status === 'Needs Review');
    }

    // 2. Search Filter (By Name)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((r) =>
        (r.name || '').toLowerCase().includes(query),
      );
    }

    // 3. Sorting
    result.sort((a, b) => {
      let aValue: any = a[sortConfig.key];
      let bValue: any = b[sortConfig.key];

      // Handle specific types for accurate sorting
      if (sortConfig.key === 'findings_count') {
        aValue = Number(a.findings_count ?? a.issues_found ?? 0);
        bValue = Number(b.findings_count ?? b.issues_found ?? 0);
      } else if (sortConfig.key === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (sortConfig.key === 'initiated_by_name') {
        aValue = (a.initiated_by_name || '').toLowerCase();
        bValue = (b.initiated_by_name || '').toLowerCase();
      } else if (sortConfig.key === 'created_at') {
        aValue = new Date(a.created_at || 0).getTime();
        bValue = new Date(b.created_at || 0).getTime();
      } else if (sortConfig.key === 'exclude_ieepa') {
        // Runs predating the column read as NULL, which must sort alongside
        // false (both mean "IEEPA was included") rather than as their own group.
        aValue = a.exclude_ieepa === true ? 1 : 0;
        bValue = b.exclude_ieepa === true ? 1 : 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [runs, activeTab, searchQuery, sortConfig]);

  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const logAppAction = useLogAppAction();

  const [userHasMonitor, setUserHasMonitor] = useState(true);
  // 'active' is the safe default, NOT 'trial'. The server's spendCredits()
  // auto-routes: a team with no paid credits for the current period but with a
  // trial pool gets charged the trial pool. spendTrialModuleCredits() has no
  // such reverse fallback — it throws NO_TRIAL_CREDITS when the team has no
  // trial_module_credits row, and the ACE finalize path swallows billing errors,
  // so the run silently completed UNBILLED. This state is also read by
  // handleRunAnalysis before the effect below resolves, so the initial value is
  // itself a billing decision.
  const [creditSpendMode, setCreditSpendMode] = useState<'trial' | 'active'>(
    'active',
  );

  // Derive the spend mode from the team's actual credit balances rather than
  // from Stripe subscription objects. This mirrors TariffAnalyzers (the sibling
  // page that already did it this way) and drops a Stripe customer lookup that
  // matched on exact team-name equality against `cur_user.stripe_cus_info` — a
  // cache populated once at login. Any divergence between the Stripe customer
  // name and the BITE team name, or a team created mid-session, resolved to no
  // customer, no subscriptions, and a silent fall-through to 'trial'.
  useEffect(() => {
    let isMounted = true;

    const resolveCreditSpendMode = async () => {
      const selectedTeamId = cur_user?.selected_team?.id;
      if (!selectedTeamId) {
        if (isMounted) setCreditSpendMode('active');
        return;
      }

      const [creditsSummaryResponse, trialSummaryResponse] = await Promise.all([
        CreditsAPI.getCreditsSummary(selectedTeamId),
        CreditsAPI.getTrialCreditsSummary(selectedTeamId),
      ]);

      const paidCreditsRemaining = Number(
        creditsSummaryResponse?.result?.remaining_credits ?? 0,
      );
      const trialSummary = Array.isArray(trialSummaryResponse?.result)
        ? trialSummaryResponse.result[0]
        : undefined;
      const hasTrialCreditPool = Number(trialSummary?.credits_total ?? 0) > 0;

      if (!isMounted) return;

      // A funded trial pool identifies a currently trialing team. Trial
      // credits are cleared when the subscription becomes active, so prefer
      // this pool even if the trial subscription also created a paid-credit
      // row containing the plan's included credits.
      if (hasTrialCreditPool) {
        setCreditSpendMode('trial');
        return;
      }

      if (paidCreditsRemaining > 0) {
        setCreditSpendMode('active');
        return;
      }

      // Neither pool has a balance. 'active' lets the server surface a real
      // NO_ACTIVE_CREDITS / INSUFFICIENT_CREDITS on the correct pool instead of
      // reporting NO_TRIAL_CREDITS for a team that was never on a trial.
      setCreditSpendMode('active');
    };

    resolveCreditSpendMode();

    return () => {
      isMounted = false;
    };
  }, [cur_user?.selected_team?.id]);

  useEffect(() => {
    checkForMonitor();
  }, []);

  const requiredTiers = ['pro', 'business', 'enterprise'];

  const checkForMonitor = () => {
    if (
      !requiredTiers.includes(cur_user.license) &&
      cur_user?.bite_super_admin !== true
    ) {
      setUserHasMonitor(false);
      return;
    }
    setUserHasMonitor(true);
  };

  const isInteractionDisabled = !userHasMonitor;
  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;

  const fetchRuns = async () => {
    if (isInteractionDisabled) {
      setRuns([]);
      setLoading(false);
      return;
    }

    const groupId = cur_user?.selected_team?.id;
    if (!groupId) return;

    setLoading(true);
    try {
      const data = await AnalyzersAPI.getAceRuns(groupId);
      setRuns(data);
    } catch (error) {
      console.error('Failed to fetch runs:', error);
    } finally {
      setLoading(false);
    }
  };

  // 1. The Initial Load (Runs exactly once when the page opens or team changes)
  useEffect(() => {
    if (isInteractionDisabled) {
      return;
    }
    fetchRuns();
  }, [cur_user?.selected_team?.id, isInteractionDisabled]);

  // 2. The "Smart Poller" (Only runs when there is active work to do)
  useEffect(() => {
    if (isInteractionDisabled) {
      return;
    }
    // Check if any run in the table is currently processing
    const hasActiveRuns = runs.some(
      (run) => run.status === 'Running' || run.status === 'Pending',
    );

    let timeoutId: NodeJS.Timeout;

    if (hasActiveRuns) {
      // If we found an active run, schedule a quiet background refresh in 5 seconds
      timeoutId = setTimeout(() => {
        fetchRuns();
      }, 5000);
    }

    // Cleanup the timeout so we don't accidentally stack them
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [runs, isInteractionDisabled]);

  // Placeholder function for analyzing the document
  const handleRunAnalysis = async (
    name: string,
    file: File,
    sourceFormat: 'es003' | 'cargowise' = 'es003',
    effectiveDate: string | null = null,
    excludeIeepa = false,
  ) => {
    if (isInteractionDisabled) {
      return;
    }

    const groupId = cur_user?.selected_team?.id;
    if (!groupId) throw new Error('No active workspace/team selected.');

    logAppAction('analyzer:run');

    try {
      // 1. Upload straight to S3 with a presigned PUT.
      //
      // ⚠️  Do NOT switch this back to the multipart route
      //    (/ace/bulk-file-upload). That route buffers the entire file in the
      //    analyzer service's heap via multer.memoryStorage() and rejects
      //    anything over 25 MB — real ES-003 roll-ups are 100-200 MB, so it
      //    returned "File too large" (surfaced as an opaque 500) for every
      //    production-sized upload. Going browser → S3 removes both the size
      //    cap and the memory pressure on the service.
      const { uploadUrl, fileKey } = await AnalyzersAPI.getAcePresignedUrl(
        name,
        groupId,
        cur_user?.selected_team?.name || '',
        file.name,
      );
      if (!uploadUrl || !fileKey) {
        throw new Error('Could not get an upload URL for the ACE document.');
      }

      await AnalyzersAPI.uploadAceFileToS3(uploadUrl, file);

      // ADD CONDITIONAL FOR TRIAL VS ACTIVE
      // 2. Tell the backend the file is ready in S3, creating the DB record and starting the job.
      //
      //    The endpoint only creates the run and queues it. An analyzer worker
      //    then reads the file, counts the exact credit cost (the run goes to
      //    'Insufficient Credits' with nothing charged if the balance is
      //    short), puts out-of-order rows in Entry Summary / Line order, and
      //    plans the chunks, so the request no longer waits on a full scan.
      // Snapshot the initiating user for the asynchronous worker. This is
      // display/audit attribution only; it is not used for authorization.
      const firstName = cur_user?.basics?.firstName?.trim?.() || '';
      const lastName = cur_user?.basics?.lastName?.trim?.() || '';
      const initiatedByName =
        `${firstName} ${lastName}`.trim() ||
        cur_user?.basics?.username ||
        cur_user?.basics?.email ||
        '';
      const initiatedById = cur_user?.basics?.id || null;
      const initiatedByEmail = cur_user?.basics?.email || null;

      await AnalyzersAPI.startAceAnalysis(
        name,
        fileKey,
        groupId,
        creditSpendMode,
        {
          id: initiatedById,
          name: initiatedByName,
          email: initiatedByEmail,
        },
        sourceFormat,
        effectiveDate,
        excludeIeepa,
      );

      setNewAnalysisModal(false);

      await fetchRuns();
    } catch (error) {
      console.error('Error uploading or starting analysis:', error);
      throw error;
    }
  };

  // 1. Triggers the modal to open
  const confirmDeleteRun = (e: React.MouseEvent, run: any) => {
    if (isInteractionDisabled) {
      return;
    }
    e.stopPropagation();
    setRunToDelete({ id: run.id, name: run.name });
    setDeleteModalOpen(true);
  };

  // BITE Findings Report (PDF) download. The PDF is built at finalize time
  // by the automations processor, so this handler is a presign-and-click —
  // no server-side regeneration. Filename matches the spec:
  //   "BITE Report {Run Name} - {MM-DD-YYYY}.pdf"
  // We swallow row-click propagation so the icon doesn't navigate to the
  // detail page on click. Disabled for any run that isn't Completed (the
  // processor only writes the PDF on success).
  const [biteReportDownloadingId, setBiteReportDownloadingId] = useState<
    string | null
  >(null);

  // Per-row Data Export + IEEPA modals. We track the selected run so the
  // same modal component handles whichever row was clicked; opening from
  // a different row swaps the selection and resets file-name input.
  const [exportRun, setExportRun] = useState<any | null>(null);
  const [exportDownloadOpts, setExportDownloadOpts] =
    useState<string>('original');
  const [exportFileName, setExportFileName] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const [ieepaRun, setIeepaRun] = useState<any | null>(null);
  const [ieepaDownloadOpts, setIeepaDownloadOpts] =
    useState<string>('cape_phase_1');
  const [ieepaFileName, setIeepaFileName] = useState('');
  const [isGeneratingIeepa, setIsGeneratingIeepa] = useState(false);

  const openExportModal = (e: React.MouseEvent, run: any) => {
    e.stopPropagation();
    if (isInteractionDisabled) return;
    setExportRun(run);
    setExportDownloadOpts('original');
    setExportFileName('');
  };

  const openIeepaModal = (e: React.MouseEvent, run: any) => {
    e.stopPropagation();
    if (isInteractionDisabled) return;
    setIeepaRun(run);
    setIeepaDownloadOpts('cape_phase_1');
    setIeepaFileName('');
  };

  // Mirrors AceRunDetailsPage.handleExecuteDownload — same docType mapping
  // and same default-name logic so the file lands identically regardless of
  // where the user kicked it off.
  const handleExecuteExport = async (customNameFromModal?: any) => {
    if (!exportRun?.id || !exportDownloadOpts) return;
    setIsExporting(true);
    try {
      const apiDocType =
        exportDownloadOpts === 'flattened'
          ? 'results_clean'
          : exportDownloadOpts;

      let defaultName = 'ACE_Export';
      if (exportDownloadOpts === 'results') defaultName = 'Analyzed Results';
      else if (exportDownloadOpts === 'flattened')
        defaultName = 'Flattened Data';
      else if (exportDownloadOpts === 'unmatched')
        defaultName = 'Unmatched Rows';
      else if (exportDownloadOpts === 'original')
        defaultName = 'Original Upload';

      const nameToUse =
        typeof customNameFromModal === 'string'
          ? customNameFromModal
          : exportFileName;
      let finalName = nameToUse?.trim() || defaultName;
      if (finalName.toLowerCase().endsWith('.csv')) {
        finalName = finalName.substring(0, finalName.length - 4);
      }
      const customFileName = `${finalName}.csv`;

      const response = await AnalyzersAPI.getS3DownloadUrl(
        exportRun.id,
        apiDocType,
        customFileName,
      );

      const link = document.createElement('a');
      link.href = response.url;
      link.setAttribute('download', customFileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      setExportRun(null);
    } catch (error) {
      console.error('Failed to download file:', error);
      enqueueSnackbar('Failed to retrieve download link from S3', {
        variant: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Mirrors AceRunDetailsPage.handleGenerateIeepaReport. Same rulesMode
  // mapping and same "no rows" handling.
  const handleGenerateIeepaForRow = async (customNameFromModal?: any) => {
    if (!ieepaRun?.id) return;
    setIsGeneratingIeepa(true);
    try {
      // Modal tile value → backend rulesMode. Phase II tiles map to the new
      // claim-centric modes; the Phase I tiles are unchanged.
      const rulesModeByOpt: Record<
        string,
        | 'eligible'
        | 'all'
        | 'ineligible'
        | 'phase_2'
        | 'phase_2_exclusions'
        | 'phase_2_rollup'
      > = {
        cape_phase_1: 'eligible',
        phase_1_exclusions: 'ineligible',
        all_ieepa: 'all',
        cape_phase_2: 'phase_2',
        phase_2_exclusions: 'phase_2_exclusions',
        cape_phase_2_rollup: 'phase_2_rollup',
      };
      const rulesMode = rulesModeByOpt[ieepaDownloadOpts] ?? 'eligible';

      // Default attachment name + the label used in the success toast, keyed
      // off the resolved mode so all five report types read consistently.
      const reportMeta: Record<typeof rulesMode, { name: string; label: string }> = {
        eligible: {
          name: 'CAPE Phase I Eligibility',
          label: 'CAPE Phase I Eligibility Report',
        },
        ineligible: {
          name: 'IEEPA Phase I Exclusions',
          label: 'IEEPA Phase I Exclusions',
        },
        all: { name: 'All IEEPA Rows', label: 'All IEEPA Rows' },
        phase_2: {
          name: 'CAPE Phase II Claim Eligibility',
          label: 'CAPE Phase II Claim Eligibility Report',
        },
        phase_2_exclusions: {
          name: 'CAPE Phase II Exclusions',
          label: 'CAPE Phase II Exclusions',
        },
        phase_2_rollup: {
          name: 'CAPE Phase II Entry Rollup',
          label: 'CAPE Phase II Entry Rollup',
        },
      };
      const defaultName = reportMeta[rulesMode].name;
      const reportLabel = reportMeta[rulesMode].label;

      const nameToUse =
        typeof customNameFromModal === 'string'
          ? customNameFromModal
          : ieepaFileName;
      let finalName = nameToUse?.trim() || defaultName;
      if (finalName.toLowerCase().endsWith('.csv')) {
        finalName = finalName.substring(0, finalName.length - 4);
      }
      const customFileName = `${finalName}.csv`;

      const { url, totalOutputRows, fileName, message } =
        await AnalyzersAPI.generateIeepaReport(ieepaRun.id, {
          rulesMode: rulesMode,
          customFileName: customFileName,
        });

      if (
        totalOutputRows === 0 ||
        message === 'No IEEPA tariffs found in this run.'
      ) {
        const emptyMessage =
          rulesMode === 'eligible' || rulesMode === 'phase_2'
            ? 'No applicable IEEPA transactions were found. You may be missing some required columns, please check your original document'
            : rulesMode === 'phase_2_rollup'
            ? 'No claimable IEEPA entries were found to roll up.'
            : rulesMode === 'ineligible' || rulesMode === 'phase_2_exclusions'
            ? 'No IEEPA rows were excluded — every IEEPA row in this run passes all eligibility rules.'
            : 'No IEEPA tariffs were found on any row in this run.';
        enqueueSnackbar(emptyMessage, { variant: 'info' });
        setIeepaRun(null);
        return;
      }

      if (!url || !fileName) {
        throw new Error(
          'Invalid download URL or file name returned from server.',
        );
      }

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar(`${reportLabel} generated (${totalOutputRows} rows)`, {
        variant: 'success',
      });
      setIeepaRun(null);
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Failed to generate IEEPA report.', { variant: 'error' });
    } finally {
      setIsGeneratingIeepa(false);
    }
  };
  // Per-row status edit menu. Anchored on whichever pencil was last
  // clicked; statusMenuRun tracks which run the selection should apply to.
  // Single shared anchor + run state keeps us out of N-renders-per-row
  // territory.
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [statusMenuRun, setStatusMenuRun] = useState<any | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);

  const openStatusMenu = (e: React.MouseEvent<HTMLElement>, run: any) => {
    e.stopPropagation();
    setStatusMenuAnchor(e.currentTarget);
    setStatusMenuRun(run);
  };

  const closeStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusMenuRun(null);
  };

  const handleStatusChange = async (
    nextStatus: 'Completed' | 'Needs Review',
  ) => {
    const target = statusMenuRun;
    closeStatusMenu();
    if (!target?.id) return;
    if (target.status === nextStatus) return;
    setStatusUpdatingId(target.id);
    try {
      const resp = await AnalyzersAPI.updateAceRunStatus(target.id, nextStatus);
      if (resp?.run?.status) {
        const newStatus = resp.run.status;
        setRuns((prev) =>
          prev.map((r) =>
            r.id === target.id ? { ...r, status: newStatus } : r,
          ),
        );
        enqueueSnackbar(`Status updated to ${newStatus}.`, {
          variant: 'success',
        });
      }
    } catch (err) {
      console.error('Failed to update run status:', err);
      enqueueSnackbar('Failed to update run status.', { variant: 'error' });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDownloadBiteReport = async (
    e: React.MouseEvent,
    run: any,
  ): Promise<void> => {
    e.stopPropagation();
    if (isInteractionDisabled || !run?.id) return;
    if (
      run.status !== 'Completed' &&
      run.status !== 'Complete' &&
      run.status !== 'Needs Review'
    ) {
      enqueueSnackbar(
        'BITE Report is available once the analysis run is completed.',
        { variant: 'info' },
      );
      return;
    }

    setBiteReportDownloadingId(run.id);
    try {
      const safeName = (run.name || 'Analysis').trim();
      const dateStr = run.created_at
        ? moment(run.created_at).format('MM-DD-YYYY')
        : moment().format('MM-DD-YYYY');
      const fileName = `BITE Report ${safeName} - ${dateStr}.pdf`;

      const { url } = await AnalyzersAPI.getBiteReportDownloadUrl(
        run.id,
        fileName,
      );

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (err) {
      console.error('Failed to download BITE Report:', err);
      enqueueSnackbar(
        'Could not download the BITE Report. Re-run the analysis if the issue persists.',
        { variant: 'error' },
      );
    } finally {
      setBiteReportDownloadingId(null);
    }
  };

  // 2. Executes the actual deletion when confirmed
  const executeDeleteRun = async () => {
    if (isInteractionDisabled) {
      return;
    }
    if (!runToDelete) return;
    setIsDeleting(true);
    try {
      await AnalyzersAPI.deleteAceRun(runToDelete.id);
      fetchRuns();
      setDeleteModalOpen(false);
      setRunToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete run:', error);
      enqueueSnackbar(
        error?.response?.data?.message ||
          'Failed to delete the run. Please try again.',
        { variant: 'error' },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusBadge = (status: string, errorMessage?: string | null) => {
    let className = 'run-badge ';
    if (status === 'Completed') className += 'completed';
    else if (status === 'Needs Review') className += 'needs-review';
    else if (status === 'Running') className += 'running';
    else if (status === 'Failed' || status === 'Insufficient Credits')
      className += 'failed';

    const badge = <span className={className}>{status}</span>;
    // The analyzer reads the file after the upload, so an unreadable file or
    // a short credit balance shows up here, as the run's reason.
    return errorMessage ? (
      <Tooltip title={errorMessage}>{badge}</Tooltip>
    ) : (
      badge
    );
  };

  // --- KPI Calculations ---
  const totalRuns = runs.length;

  const totalFindings = runs.reduce(
    (sum, run) => sum + (Number(run.findings_count) || 0),
    0,
  );

  // Assuming "Needs Review" means runs that either explicitly have that status,
  // or are 'Completed' but came back with flags/findings.
  const needsReviewCount = runs.filter(
    (run) => run.status === 'Needs Review',
  ).length;

  const inProgressCount = runs.filter(
    (run) => run.status === 'Running' || run.status === 'Pending',
  ).length;

  // Filter Data based on Tab
  const filteredRuns =
    activeTab === 'All Runs'
      ? runs
      : runs.filter((run) => run.status === activeTab);

  // Pagination Logic
  const itemsPerPage = viewMode === 'list' ? 10 : 9;
  const totalPages = Math.ceil(filteredRuns.length / itemsPerPage) || 1;

  const paginatedRuns = processedRuns.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Reset to page 1 if tab or view mode changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, viewMode]);

  const handlePageChange = (e: ChangeEvent<unknown>, new_page: number) => {
    setCurrentPage(new_page);
  };

  return (
    <>
      <div className='analyzers-dashboard-root'>
        <div className='detail-header-flex'>
          <div>
            <h1 className='analyzers-page-title'>ACE Analyzer</h1>
            <p className='analyzers-page-subtitle'>
              Track and manage all your ACE compliance analysis runs.
            </p>
          </div>
          <div className='detail-header-actions'>
            <button
              className='btn-primary'
              onClick={() => {
                if (!isInteractionDisabled) {
                  setNewAnalysisModal(true);
                }
              }}
              disabled={isInteractionDisabled}
              style={{
                cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                opacity: isInteractionDisabled ? 0.7 : 1,
              }}
            >
              {isInteractionDisabled && (
                <LockIcon style={{ fontSize: 16, marginRight: 6 }} />
              )}
              <BoltIcon style={{ fontSize: 18 }} /> Run New Analysis
            </button>
          </div>
        </div>

        <RequiredLicense userLicense={normalizedLicense} minimumLicense='pro' />

        {/* KPI Row */}
        <div className='detail-kpi-grid'>
          <div className='detail-kpi-card'>
            <div className='kpi-card-header'>
              <BarChartIcon style={{ fontSize: 16, color: '#4895ef' }} />
              <span className='kpi-card-label'>Total Runs</span>
            </div>
            <div className='kpi-card-value text-blue'>
              {totalRuns.toLocaleString()}
            </div>
          </div>

          <div className='detail-kpi-card'>
            <div className='kpi-card-header'>
              <TextSnippetIcon style={{ fontSize: 16, color: '#20c997' }} />
              <span className='kpi-card-label'>Total Findings</span>
            </div>
            <div className='kpi-card-value text-teal'>
              {totalFindings.toLocaleString()}
            </div>
          </div>

          <div className='detail-kpi-card'>
            <div className='kpi-card-header'>
              <WarningAmberIcon style={{ fontSize: 16, color: '#f79009' }} />
              <span className='kpi-card-label'>Needs Review</span>
            </div>
            <div className='kpi-card-value text-orange'>
              {needsReviewCount.toLocaleString()}
            </div>
          </div>

          <div className='detail-kpi-card'>
            <div className='kpi-card-header'>
              <AccessTimeIcon style={{ fontSize: 16, color: '#4895ef' }} />
              <span className='kpi-card-label'>In Progress</span>
            </div>
            <div className='kpi-card-value text-blue'>
              {inProgressCount.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Dropdown with adjacent button */}
        <div
          style={{
            width: '100%',
            padding: '0 24px',
            boxSizing: 'border-box',
            display: 'flex', // Enable flex layout
            gap: '16px', // Spacing between items
            alignItems: 'center', // Align vertically
          }}
        >
          {/* Main Dropdown Guide */}
          <button
            type='button'
            onClick={() => {
              if (!isInteractionDisabled) {
                setShowAceGuide((prev) => !prev);
              }
            }}
            disabled={isInteractionDisabled}
            style={{
              flex: 1, // Take up remaining space, pushing the adjacent button to the right
              background: 'var(--Background-200, var(--bg-colors))',
              border: '1px solid var(--es-soft-border)',
              borderRadius: '8px',
              minHeight: '44px',
              color: 'var(--text-primary)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 14px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px',
                fontWeight: 500,
              }}
            >
              <HelpOutlineOutlinedIcon
                sx={{ fontSize: 18, color: 'var(--Primary-400, #4895ef)' }}
              />
              New to the ACE Analyzer? Learn how the analysis works and view
              document export details
            </span>
            {showAceGuide ? (
              <ExpandLessOutlinedIcon
                sx={{ fontSize: 20, color: 'var(--card-text)' }}
              />
            ) : (
              <ExpandMoreOutlinedIcon
                sx={{ fontSize: 20, color: 'var(--card-text)' }}
              />
            )}
          </button>

          {/* IEEPA Refund Eligibility Information Button */}
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#10b981',
              border: '1px solid var(--es-soft-border)',
              borderRadius: '6px',
              background: 'var(--bg-secondary, #1f2937)',
              height: '44px', // Match dropdown height
              padding: '0 16px',
              cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
              opacity: isInteractionDisabled ? 0.6 : 1,
              whiteSpace: 'nowrap',
              fontFamily: 'inherit',
              fontSize: '14px',
              fontWeight: 500,
            }}
            onClick={() => setIeepaInfoModalOpen(true)}
            disabled={isInteractionDisabled}
          >
            <AttachMoneyIcon style={{ fontSize: 16 }} /> IEEPA Refund
            Information
          </button>
        </div>

        {/* Disclaimer Text */}
        <div
          style={{
            width: '100%',
            padding: '0 24px',
            boxSizing: 'border-box',
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: '12px',
              color: 'var(--text-secondary, #9ca3af)',
              lineHeight: '1.5',
            }}
          >
            This tool and all outputs are provided “as is” without warranty of
            any kind, whether express or implied, including but not limited to
            warranties of accuracy, completeness, or fitness for a particular
            purpose. The calculations are based on user-provided ACE ES003
            report data and may contain errors, omissions, or inaccuracies.
            Under no circumstances shall BITE Data Corporation be liable for any
            direct, indirect, incidental, or consequential damages arising from
            the use of, or reliance on, this tool or its outputs. Users are
            solely responsible for independently verifying all results and for
            any decisions, filings, or claims made based on this information.
            This tool does not replace official CBP determinations or filings
            and should not be relied upon as the sole basis for submitting IEEPA
            refund claims.
          </p>
        </div>

        {showAceGuide && (
          <div
            style={{
              width: '100%',
              padding: '0 24px',
              boxSizing: 'border-box',
              display: 'flex',
              gap: '16px',
              marginTop: '16px',
              flexWrap: 'wrap',
            }}
          >
            {/* Left Card: Required Fields & Upload Tips */}
            <div
              style={{
                flex: '1 1 420px',
                background: 'var(--Background-200, var(--bg-colors))',
                border: '1px solid var(--base-border)',
                borderRadius: '10px',
                padding: '18px',
              }}
            >
              <div
                className='api-container-left-h1'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '18px',
                  marginBottom: '6px',
                  paddingTop: '0px',
                }}
              >
                <FactCheckOutlinedIcon
                  sx={{ fontSize: 18, color: '#20c997' }}
                />
                Required Upload Fields
              </div>
              <div
                className='card-db-meta-span'
                style={{
                  display: 'block',
                  marginBottom: '16px',
                  paddingTop: '0px',
                  lineHeight: '1.5',
                }}
              >
                The tool requires certain fields to map the data and execute the
                BITE calculations.{' '}
                <strong>
                  Note: Uploads are limited to a maximum of 50,000 rows per
                  file.
                </strong>
              </div>

              <ul
                className='card-db-meta-span'
                style={{
                  margin: '0 0 0 20px',
                  lineHeight: '1.6',
                  padding: 0,
                }}
              >
                <li style={{ marginBottom: '8px' }}>
                  <strong style={{ color: 'var(--text-primary-color)' }}>
                    Entry & Line Numbers:
                  </strong>{' '}
                  Used by the flattening logic to securely group multiple rows
                  (Base HTS + Chapter 98/99 tariffs) into a single cohesive line
                  item.
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong style={{ color: 'var(--text-primary-color)' }}>
                    HTS Number:
                  </strong>{' '}
                  The core data used by the BITE engine to look up expected base
                  rates, fees, and trade remedies.
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong style={{ color: 'var(--text-primary-color)' }}>
                    Country of Origin:
                  </strong>{' '}
                  Critical for determining Column 2 status, FTA eligibility, and
                  specific tariffs (e.g., AD/CVD, Section 232/301).
                </li>
                <li>
                  <strong style={{ color: 'var(--text-primary-color)' }}>
                    Value & Duty Amounts:
                  </strong>{' '}
                  Compared directly against BITE&apos;s calculated expectations
                  to identify and flag discrepancies.
                </li>
              </ul>
            </div>

            {/* Right Card: Generated Documents */}
            <div
              style={{
                flex: '1 1 420px',
                background: 'var(--Background-200, var(--bg-colors))',
                border: '1px solid var(--base-border)',
                borderRadius: '10px',
                padding: '18px',
              }}
            >
              <div
                className='api-container-left-h1'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '18px',
                  marginBottom: '6px',
                  paddingTop: '0px',
                }}
              >
                <LibraryBooksOutlinedIcon
                  sx={{ fontSize: 18, color: '#f79009' }}
                />
                Exported Documents Guide
              </div>
              <div
                className='card-db-meta-span'
                style={{ lineHeight: '1.5', marginBottom: '16px' }}
              >
                When an analysis completes, several documents are generated for
                your review.
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    borderLeft: '3px solid #4895ef',
                    paddingLeft: '12px',
                  }}
                >
                  <div
                    style={{
                      color: 'var(--text-primary-color)',
                      fontWeight: 500,
                      fontSize: '14px',
                      marginBottom: '2px',
                    }}
                  >
                    Flattened Data (Clean)
                  </div>
                  <div
                    className='card-db-meta-span'
                    style={{ fontSize: '13px' }}
                  >
                    Transforms the vertical CBP format into a clean, horizontal
                    view with all related tariffs appended securely to their
                    parent line item.
                  </div>
                </div>
                <div
                  style={{
                    borderLeft: '3px solid #20c997',
                    paddingLeft: '12px',
                  }}
                >
                  <div
                    style={{
                      color: 'var(--text-primary-color)',
                      fontWeight: 500,
                      fontSize: '14px',
                      marginBottom: '2px',
                    }}
                  >
                    Analyzed Results
                  </div>
                  <div
                    className='card-db-meta-span'
                    style={{ fontSize: '13px' }}
                  >
                    The flattened view with appended <i>BITE Discrepancy</i>{' '}
                    columns, immediately highlighting unexpected tariffs,
                    missing codes, or duty variances.
                  </div>
                </div>
                <div
                  style={{
                    borderLeft: '3px solid #f04438',
                    paddingLeft: '12px',
                  }}
                >
                  <div
                    style={{
                      color: 'var(--text-primary-color)',
                      fontWeight: 500,
                      fontSize: '14px',
                      marginBottom: '2px',
                    }}
                  >
                    Unmatched Rows
                  </div>
                  <div
                    className='card-db-meta-span'
                    style={{ fontSize: '13px' }}
                  >
                    Isolates structural outliers from your dataset (e.g., tariff
                    rows missing a base HTS, or duplicate base codes) so your
                    main data remains pristine.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Runs Section */}
        <div className='analyzers-section'>
          <div className='analyzers-section-header'>
            <h2 className='section-title'>Analysis Runs</h2>
            <p className='section-subtitle'>
              Click a run to view its full findings and detail.
            </p>
          </div>

          {/* Toolbar with Tabs and View Toggle */}
          <div
            className='runs-toolbar'
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '16px',
            }}
          >
            <div className='runs-tabs'>
              <div className='data-search-wrapper'>
                <SearchIcon
                  className='data-search-icon'
                  style={{ fontSize: 18 }}
                />
                <input
                  type='text'
                  className='data-search-input'
                  placeholder='Search by Name'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {['All Runs', 'Completed', 'Needs Review', 'Running'].map(
                (tab) => (
                  <button
                    key={tab}
                    className={`runs-tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => {
                      if (!isInteractionDisabled) {
                        setActiveTab(tab);
                      }
                    }}
                    disabled={isInteractionDisabled}
                    style={{
                      cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                      opacity: isInteractionDisabled ? 0.65 : 1,
                    }}
                  >
                    {tab}
                  </button>
                ),
              )}
            </div>

            <div className='view-toggle-group'>
              <button
                className={`view-toggle-btn ${
                  viewMode === 'list' ? 'active' : ''
                }`}
                onClick={() => setViewMode('list')}
                title='List View'
                disabled={isInteractionDisabled}
                style={{
                  cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                  opacity: isInteractionDisabled ? 0.65 : 1,
                }}
              >
                <ViewListIcon style={{ fontSize: 20 }} />
              </button>
              <button
                className={`view-toggle-btn ${
                  viewMode === 'card' ? 'active' : ''
                }`}
                onClick={() => setViewMode('card')}
                title='Card View'
                disabled={isInteractionDisabled}
                style={{
                  cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                  opacity: isInteractionDisabled ? 0.65 : 1,
                }}
              >
                <GridViewIcon style={{ fontSize: 20 }} />
              </button>
            </div>
          </div>

          {/* Content Container (Table or Grid) */}
          <div
            className='runs-content-container'
            style={
              viewMode === 'card'
                ? { background: 'transparent', border: 'none' }
                : {}
            }
          >
            {/* List View */}
            {viewMode === 'list' && (
              <table className='runs-table'>
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort('name')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        Run Name
                        {sortConfig.key === 'name' &&
                          (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          ))}
                      </div>
                    </th>
                    <th>Status</th>
                    <th
                      onClick={() => handleSort('initiated_by_name')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        Uploaded By
                        {sortConfig.key === 'initiated_by_name' &&
                          (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          ))}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('findings_count')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        Findings
                        {sortConfig.key === 'findings_count' &&
                          (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          ))}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('exclude_ieepa')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        IEEPA
                        {sortConfig.key === 'exclude_ieepa' &&
                          (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          ))}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('created_at')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        Date
                        {sortConfig.key === 'created_at' &&
                          (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          ))}
                      </div>
                    </th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRuns.map((run) => (
                    <tr
                      key={run.id}
                      onClick={() => {
                        if (!isInteractionDisabled) {
                          history.push(`/ace-analyzers/${run.id}`);
                        }
                      }}
                      style={{
                        cursor: isInteractionDisabled
                          ? 'not-allowed'
                          : 'pointer',
                      }}
                      className='hoverable-row'
                    >
                      <td className='run-name'>{run.name}</td>
                      <td>
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          {getStatusBadge(run.status, run.error_message)}
                          {/* Edit pencil — only for settled rows. Stops
                              click propagation so opening the menu doesn't
                              also navigate the user into the detail page. */}
                          {(run.status === 'Completed' ||
                            run.status === 'Complete' ||
                            run.status === 'Needs Review') && (
                            <Tooltip title='Edit status'>
                              <span>
                                <button
                                  onClick={(e) => openStatusMenu(e, run)}
                                  disabled={
                                    isInteractionDisabled ||
                                    statusUpdatingId === run.id
                                  }
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    cursor:
                                      isInteractionDisabled ||
                                      statusUpdatingId === run.id
                                        ? 'not-allowed'
                                        : 'pointer',
                                    padding: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                  aria-label='Edit status'
                                >
                                  <EditIcon style={{ fontSize: 14 }} />
                                </button>
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                      <td>{run.initiated_by_name || '--'}</td>
                      <td>{run.findings_count}</td>
                      <td>
                        {/* Only the opted-out state is worth a chip. Runs that
                            included IEEPA (the default, and every run predating
                            the column) get the same muted dash the other
                            optional columns use, so the column stays quiet. */}
                        {run.exclude_ieepa === true ? (
                          <Tooltip title='This run was analyzed with all IEEPA tariffs removed'>
                            <span className='screening-badge lens-chip chip-yellow'>
                              Excluded
                            </span>
                          </Tooltip>
                        ) : (
                          '--'
                        )}
                      </td>
                      <td>
                        {run?.created_at
                          ? moment(run.created_at).format('MM/DD/YYYY')
                          : '--'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '16px',
                            height: '100%',
                          }}
                        >
                          {/* Data Export — opens the same DownloadModal
                              (s3 mode) the details page uses. Enabled for
                              any *settled* run (Completed OR Needs Review,
                              since both end the processor with the same
                              S3 artifacts). Disabled for Pending / Running
                              / Failed where the S3 keys don't exist yet. */}
                          {(() => {
                            const isSettled =
                              run.status === 'Completed' ||
                              run.status === 'Complete' ||
                              run.status === 'Needs Review';
                            const disabled =
                              isInteractionDisabled || !isSettled;
                            return (
                              <Tooltip
                                title={
                                  isSettled
                                    ? 'Data Export'
                                    : 'Data Export is available after the run completes'
                                }
                              >
                                <span>
                                  <button
                                    onClick={(e) => openExportModal(e, run)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: 'var(--text-primary-color)',
                                      cursor: disabled
                                        ? 'not-allowed'
                                        : 'pointer',
                                      padding: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      opacity: disabled ? 0.4 : 1,
                                    }}
                                    disabled={disabled}
                                  >
                                    <IosShareIcon style={{ fontSize: 24 }} />
                                  </button>
                                </span>
                              </Tooltip>
                            );
                          })()}

                          {/* IEEPA Report (CSV) — opens DownloadModal in
                              ieepa mode for the row. Same gating as the
                              data export icon. */}
                          {(() => {
                            const isSettled =
                              run.status === 'Completed' ||
                              run.status === 'Complete' ||
                              run.status === 'Needs Review';
                            const disabled =
                              isInteractionDisabled || !isSettled;
                            return (
                              <Tooltip
                                title={
                                  isSettled
                                    ? 'Generate IEEPA Report'
                                    : 'IEEPA Report is available after the run completes'
                                }
                              >
                                <span>
                                  <button
                                    onClick={(e) => openIeepaModal(e, run)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#10b981',
                                      cursor: disabled
                                        ? 'not-allowed'
                                        : 'pointer',
                                      padding: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      opacity: disabled ? 0.4 : 1,
                                    }}
                                    disabled={disabled}
                                  >
                                    <FileDownloadOutlinedIcon
                                      style={{ fontSize: 24 }}
                                    />
                                  </button>
                                </span>
                              </Tooltip>
                            );
                          })()}

                          {/* BITE Findings Report (PDF) download. The icon is
                              shown for every row but only enabled once the
                              run is settled (Completed OR Needs Review —
                              both reach finalize and produce the PDF).
                              Disabled for Pending/Running/Failed rows. */}
                          {(() => {
                            const isSettled =
                              run.status === 'Completed' ||
                              run.status === 'Complete' ||
                              run.status === 'Needs Review';
                            const isDownloadingThis =
                              biteReportDownloadingId === run.id;
                            const disabled =
                              isInteractionDisabled ||
                              !isSettled ||
                              isDownloadingThis;
                            return (
                              <Tooltip
                                title={
                                  isSettled
                                    ? 'Download BITE Report (PDF)'
                                    : 'BITE Report is available after the run completes'
                                }
                              >
                                <span>
                                  <button
                                    onClick={(e) =>
                                      handleDownloadBiteReport(e, run)
                                    }
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#4895ef',
                                      cursor: disabled
                                        ? 'not-allowed'
                                        : 'pointer',
                                      padding: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      opacity: disabled ? 0.4 : 1,
                                    }}
                                    disabled={disabled}
                                  >
                                    <PictureAsPdfOutlinedIcon
                                      style={{ fontSize: 24 }}
                                    />
                                  </button>
                                </span>
                              </Tooltip>
                            );
                          })()}
                          <Tooltip title='Delete Analysis'>
                            <span>
                              <button
                                onClick={(e) => confirmDeleteRun(e, run)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: 'var(--red-color, #ef4444)',
                                  cursor: isInteractionDisabled
                                    ? 'not-allowed'
                                    : 'pointer',
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  opacity: isInteractionDisabled ? 0.5 : 1,
                                }}
                                disabled={isInteractionDisabled}
                              >
                                <DeleteOutlineIcon style={{ fontSize: 24 }} />
                              </button>
                            </span>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {paginatedRuns.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        style={{ textAlign: 'center', padding: '24px' }}
                      >
                        No runs found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Card View */}
            {viewMode === 'card' && (
              <div className='card-grid-three'>
                {paginatedRuns.map((run) => (
                  <div
                    key={run.id}
                    className='card-db hoverable-card' // <-- Added new hover class
                    onClick={() => {
                      if (!isInteractionDisabled) {
                        history.push(`/ace-analyzers/${run.id}`);
                      }
                    }} // <-- Whole card is clickable
                    style={{
                      minHeight: '160px',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                      opacity: isInteractionDisabled ? 0.8 : 1,
                    }}
                  >
                    <div
                      className='card-db-header'
                      style={{
                        marginBottom: '16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div>
                        <div
                          className='card-db-title'
                          style={{
                            fontSize: '16px',
                            lineHeight: '1.4',
                            marginBottom: '8px',
                          }}
                        >
                          {run.name}
                        </div>
                        {getStatusBadge(run.status, run.error_message)}
                      </div>

                      {/* New Delete Button positioned at the top right of the card */}
                      <button
                        onClick={(e) => confirmDeleteRun(e, run)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--red-color, #ef4444)',
                          cursor: isInteractionDisabled
                            ? 'not-allowed'
                            : 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          opacity: isInteractionDisabled ? 0.5 : 1,
                        }}
                        title='Delete Analysis'
                        disabled={isInteractionDisabled}
                      >
                        <DeleteOutlineIcon style={{ fontSize: 24 }} />
                      </button>
                    </div>

                    <div
                      className='card-db-meta-col'
                      style={{
                        alignItems: 'flex-start',
                        gap: '8px',
                        marginBottom: '16px',
                      }}
                    >
                      <span
                        className='card-db-meta-span'
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <span>Findings:</span>
                        <strong style={{ color: 'var(--text-primary-color)' }}>
                          {run.findings_count}
                        </strong>
                      </span>
                      <span
                        className='card-db-meta-span'
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <span>Date:</span>
                        <strong style={{ color: 'var(--text-primary-color)' }}>
                          {run?.created_at
                            ? moment(run.created_at).format('MM/DD/YYYY')
                            : '--'}
                        </strong>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination Footer - Matches existing MUI setup */}
            {filteredRuns.length > 0 && (
              <div
                className='hb-pagination'
                style={
                  viewMode === 'list'
                    ? {
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '16px',
                        flexWrap: 'wrap',
                        padding: '16px 24px',
                        borderTop: '1px solid var(--es-soft-border)',
                        background: 'var(--Background-200, var(--bg-colors))',
                      }
                    : {
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '16px',
                        marginTop: '24px',
                      }
                }
              >
                {viewMode === 'list' && (
                  <span
                    className='pagination-text'
                    style={{ color: 'var(--text-secondary)', fontSize: '14px' }}
                  >
                    Page {currentPage} of {totalPages}
                  </span>
                )}
                <Pagination
                  onChange={handlePageChange}
                  size='large'
                  count={totalPages}
                  shape='rounded'
                  variant='text'
                  page={currentPage}
                  disabled={isInteractionDisabled}
                />
              </div>
            )}
          </div>
        </div>
        <RunAnalysisModal
          open={newAnalysisModal}
          handleClose={() => setNewAnalysisModal(false)}
          onAnalyze={handleRunAnalysis}
          cur_user={cur_user}
          creditSpendMode={creditSpendMode}
        />
        {/* NEW: IEEPA info modal component instantiation */}
        <IeepaInfoModal
          open={ieepaInfoModalOpen}
          handleClose={() => setIeepaInfoModalOpen(false)}
        />
        {/* Data Export modal — per-row trigger from the Actions column.
            Reuses the same DownloadModal the details page uses (s3 mode). */}
        <Modal
          open={!!exportRun}
          onClose={() => !isExporting && setExportRun(null)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ outline: 'none' }}>
            <DownloadModal
              handleClose={() => setExportRun(null)}
              setFileName={setExportFileName}
              downloadOpts={exportDownloadOpts}
              setDownloadOpts={setExportDownloadOpts}
              handleCSV={handleExecuteExport}
              triggerCSV={isExporting}
              mode='s3'
              hasDiscrepancies={(exportRun?.findings_count ?? 0) > 0}
              hasUnmatched={(exportRun?.unmatched_count ?? 0) > 0}
              isOpen={!!exportRun}
            />
          </div>
        </Modal>

        {/* IEEPA Report modal — per-row trigger. Same DownloadModal in
            ieepa mode; emits { name }, we forward the raw string. */}
        <Modal
          open={!!ieepaRun}
          onClose={() => !isGeneratingIeepa && setIeepaRun(null)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ outline: 'none' }}>
            <DownloadModal
              handleClose={() => setIeepaRun(null)}
              setFileName={setIeepaFileName}
              downloadOpts={ieepaDownloadOpts}
              setDownloadOpts={setIeepaDownloadOpts}
              handleCSV={(payload: any) =>
                handleGenerateIeepaForRow(payload?.name)
              }
              triggerCSV={isGeneratingIeepa}
              mode='ieepa'
              isOpen={!!ieepaRun}
            />
          </div>
        </Modal>

        {/* Shared status-edit menu. Anchored on whichever pencil was last
            clicked; the row identity travels via statusMenuRun so a single
            Menu serves every row. */}
        <Menu
          anchorEl={statusMenuAnchor}
          open={!!statusMenuAnchor}
          onClose={closeStatusMenu}
        >
          <MenuItem
            selected={
              statusMenuRun?.status === 'Completed' ||
              statusMenuRun?.status === 'Complete'
            }
            onClick={() => handleStatusChange('Completed')}
          >
            Completed
          </MenuItem>
          <MenuItem
            selected={statusMenuRun?.status === 'Needs Review'}
            onClick={() => handleStatusChange('Needs Review')}
          >
            Needs Review
          </MenuItem>
        </Menu>

        <DeleteConfirmationModal
          open={deleteModalOpen}
          handleClose={() => {
            setDeleteModalOpen(false);
            setRunToDelete(null);
          }}
          onDelete={executeDeleteRun}
          isDeleting={isDeleting}
          runName={runToDelete?.name}
        />
      </div>
    </>
  );
};

export default AceAnalyzerPage;
